/**
 * Custom hook for user management action handlers.
 * Extracted from UsersManagement to keep component files under 500 lines.
 *
 * @returns Action handlers, state setters, and loading states for user management
 */

import { useState, useCallback, useEffect } from 'react';
import { coreAPI, UserProfile, Role, UserManagementLog, Site, UserSitePermission } from '../../lib/api';
import { getApiErrorMessage } from '../../lib/api/types/common';
import { toast } from 'sonner';

interface UseUserActionsReturn {
  // Data
  users: UserProfile[];
  availableRoles: Role[];
  activityLogs: UserManagementLog[];
  userSitePermissions: UserSitePermission[];
  // State
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
  selectedUser: UserProfile | null;
  selectedRoleId: number | null;
  savingPermission: number | null;
  userDetailTab: 'profile' | 'companies';
  // Modal visibility
  showUserDetails: boolean;
  showChangeRoleDialog: boolean;
  showActivityLogDialog: boolean;
  showBulkApproveDialog: boolean;
  showBulkInviteDialog: boolean;
  // Setters
  setError: (error: string | null) => void;
  setSelectedUser: (user: UserProfile | null) => void;
  setSelectedRoleId: (id: number | null) => void;
  setUserDetailTab: (tab: 'profile' | 'companies') => void;
  setShowUserDetails: (show: boolean) => void;
  setShowChangeRoleDialog: (show: boolean) => void;
  setShowActivityLogDialog: (show: boolean) => void;
  setShowBulkApproveDialog: (show: boolean) => void;
  setShowBulkInviteDialog: (show: boolean) => void;
  // Actions
  fetchUsers: () => Promise<void>;
  handleToggleActive: (userProfile: UserProfile) => Promise<void>;
  handleSafeDelete: (userProfile: UserProfile) => Promise<void>;
  handleChangeRole: () => Promise<void>;
  handleToggleAdmin: (userProfile: UserProfile, isAdmin: boolean) => Promise<void>;
  handleSendInvitation: (userProfile: UserProfile) => Promise<void>;
  handleViewActivityLog: (userProfile: UserProfile) => Promise<void>;
  openChangeRoleDialog: (userProfile: UserProfile) => void;
  openUserDetails: (userProfile: UserProfile) => Promise<void>;
  handleToggleSiteLink: (siteId: number, isLinked: boolean) => Promise<void>;
  handleToggleSitePermission: (siteId: number, permission: string, value: boolean) => Promise<void>;
  handleApproveUser: (userId: number) => Promise<void>;
  handleRejectUser: (userId: number) => Promise<void>;
  handleBulkApprove: (userIds: number[]) => Promise<void>;
  handleBulkSendInvitations: (profileIds: number[]) => Promise<void>;
  fetchAvailableRoles: () => Promise<void>;
}

/**
 * Hook encapsulating all user management API actions and related state.
 *
 * @returns Object with action handlers, data, and UI state for user management
 */
export function useUserActions(): UseUserActionsReturn {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [activityLogs, setActivityLogs] = useState<UserManagementLog[]>([]);
  const [userSitePermissions, setUserSitePermissions] = useState<UserSitePermission[]>([]);
  const [_companySites, setCompanySites] = useState<Site[]>([]);

  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingPermission, setSavingPermission] = useState<number | null>(null);

  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [userDetailTab, setUserDetailTab] = useState<'profile' | 'companies'>('profile');

  const [showUserDetails, setShowUserDetails] = useState(false);
  const [showChangeRoleDialog, setShowChangeRoleDialog] = useState(false);
  const [showActivityLogDialog, setShowActivityLogDialog] = useState(false);
  const [showBulkApproveDialog, setShowBulkApproveDialog] = useState(false);
  const [showBulkInviteDialog, setShowBulkInviteDialog] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const response = await coreAPI.getUserProfiles({ page_size: 1000 });
      setUsers(response.data.results || []);
    } catch (err: unknown) {
      console.error('Error fetching users:', err);
      setError(getApiErrorMessage(err, 'Failed to load users'));
    } finally { setLoading(false); }
  }, []);

  const fetchAvailableRoles = useCallback(async () => {
    try {
      const response = await coreAPI.getAvailableRoles();
      setAvailableRoles(response.data.roles || []);
    } catch (err: unknown) {
      console.error('Error fetching roles:', err);
    }
  }, []);

  useEffect(() => {
    void fetchAvailableRoles();
  }, [fetchAvailableRoles]);

  const handleToggleActive = useCallback(async (userProfile: UserProfile) => {
    try {
      setActionLoading(true); setError(null);
      const response = await coreAPI.toggleUserActive(userProfile.id);
      const newIsActive = response.data.is_active;
      await fetchUsers();
      setSelectedUser((prev) =>
        prev?.id === userProfile.id ? { ...prev, user: { ...prev.user, is_active: newIsActive } } : prev
      );
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to update user status'));
    } finally { setActionLoading(false); }
  }, [fetchUsers]);

  // CR-5312: backend decides delete-vs-deactivate based on the user's
  // login/audit footprint; we just surface the right toast.
  const handleSafeDelete = useCallback(async (userProfile: UserProfile) => {
    try {
      setActionLoading(true); setError(null);
      const response = await coreAPI.safeDeleteUser(userProfile.id);
      const { deleted, deactivated, message } = response.data;
      if (deleted) toast.success(message || `User ${userProfile.user.email} deleted`);
      else if (deactivated) toast.success(message || `User ${userProfile.user.email} deactivated`);
      else toast.message(message || 'No change');
      await fetchUsers();
      // Close any open detail modal pointing at the deleted user.
      setSelectedUser((prev) => (prev?.id === userProfile.id && deleted ? null : prev));
      if (deleted) setShowUserDetails(false);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to delete user'));
    } finally { setActionLoading(false); }
  }, [fetchUsers]);

  // CR-5294: surface success/failure via toast — the page-level error
  // banner sits behind the modal backdrop, which made failed role changes
  // look like the popup did nothing.
  const handleChangeRoleImpl = async () => {
    if (!selectedUser || !selectedRoleId) return;
    try {
      setActionLoading(true); setError(null);
      const response = await coreAPI.changeUserRole(selectedUser.id, selectedRoleId);
      await fetchUsers();
      setSelectedUser(response.data.user);
      setShowChangeRoleDialog(false); setSelectedRoleId(null);
      toast.success(response.data.message || 'Role updated');
    } catch (err: unknown) {
      const message = getApiErrorMessage(err, 'Failed to change user role');
      setError(message);
      toast.error(message);
    } finally { setActionLoading(false); }
  };

  const handleToggleAdmin = useCallback(async (userProfile: UserProfile, isAdmin: boolean) => {
    try {
      setActionLoading(true); setError(null);
      await coreAPI.toggleUserAdmin(userProfile.id, isAdmin);
      await fetchUsers();
      setSelectedUser((prev) =>
        prev?.id === userProfile.id
          ? { ...prev, user: { ...prev.user, is_superuser: isAdmin, is_staff: isAdmin } }
          : prev
      );
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to update admin status'));
    } finally { setActionLoading(false); }
  }, [fetchUsers]);

  const handleSendInvitation = useCallback(async (userProfile: UserProfile) => {
    try {
      setActionLoading(true); setError(null);
      const response = await coreAPI.sendInvitationEmail(userProfile.id);
      toast.success(`Invitation email sent to ${userProfile.user.email}`);
      if (response.data.user) {
        const updated = response.data.user;
        setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
        setSelectedUser((prev) => (prev?.id === updated.id ? updated : prev));
      } else {
        await fetchUsers();
      }
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to send invitation email'));
    } finally { setActionLoading(false); }
  }, [fetchUsers]);

  const handleViewActivityLog = useCallback(async (userProfile: UserProfile) => {
    try {
      setSelectedUser(userProfile); setActionLoading(true);
      const response = await coreAPI.getUserManagementLogs(userProfile.id);
      setActivityLogs(response.data || []);
      setShowActivityLogDialog(true);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to load activity logs'));
    } finally { setActionLoading(false); }
  }, []);

  const openChangeRoleDialog = useCallback((userProfile: UserProfile) => {
    setSelectedUser(userProfile);
    setSelectedRoleId(userProfile.role);
    setShowChangeRoleDialog(true);
  }, []);

  const openUserDetails = useCallback(async (userProfile: UserProfile) => {
    setSelectedUser(userProfile); setUserDetailTab('profile');
    setShowUserDetails(true); setUserSitePermissions([]); setCompanySites([]);
    try {
      const [sitesResponse, permissionsResponse] = await Promise.all([
        coreAPI.getSites({ company: userProfile.company }),
        coreAPI.getUserSitePermissions(userProfile.id)
      ]);
      setCompanySites(sitesResponse.data.results || []);
      setUserSitePermissions(permissionsResponse.data || []);
    } catch (err) {
      console.error('Error fetching site permissions:', err);
      setCompanySites([]); setUserSitePermissions([]);
    }
  }, []);

  const handleToggleSiteLink = async (siteId: number, isLinked: boolean) => {
    if (!selectedUser) return;
    setSavingPermission(siteId);
    try {
      const response = await coreAPI.updateUserSitePermission(selectedUser.id, siteId, {
        is_linked: isLinked, is_active: isLinked, can_purchasing: isLinked,
      });
      setUserSitePermissions(prev => prev.map(p => p.site === siteId ? response.data : p));
      await fetchUsers();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to update site link'));
    } finally { setSavingPermission(null); }
  };

  const handleToggleSitePermission = async (siteId: number, permission: string, value: boolean) => {
    if (!selectedUser) return;
    setSavingPermission(siteId);
    try {
      // CR-5903: Administrator / Approval and Read Only are mutually exclusive.
      // When enabling one, clear the conflicting flags in the same request so
      // existing inconsistent state cannot persist.
      const payload: Record<string, boolean> = { [permission]: value };
      if (value) {
        if (permission === 'is_admin' || permission === 'can_approve') {
          payload['is_read_only'] = false;
        } else if (permission === 'is_read_only') {
          payload['is_admin'] = false;
          payload['can_approve'] = false;
        }
      }
      const response = await coreAPI.updateUserSitePermission(selectedUser.id, siteId, payload);
      setUserSitePermissions(prev => prev.map(p => p.site === siteId ? response.data : p));
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to update permission'));
    } finally { setSavingPermission(null); }
  };

  const handleApproveUser = useCallback(async (userId: number) => {
    try {
      setActionLoading(true); setError(null);
      await coreAPI.approveUser(userId);
      await fetchUsers();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to approve user'));
    } finally { setActionLoading(false); }
  }, [fetchUsers]);

  const handleRejectUser = useCallback(async (userId: number) => {
    try {
      setActionLoading(true); setError(null);
      await coreAPI.rejectUser(userId);
      toast.success('User rejected and deactivated');
      await fetchUsers();
      setSelectedUser(null);
      setShowUserDetails(false);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to reject user'));
    } finally { setActionLoading(false); }
  }, [fetchUsers]);

  const handleBulkApprove = useCallback(async (userIds: number[]) => {
    try {
      setActionLoading(true); setError(null);
      await coreAPI.bulkApproveUsers(userIds);
      await fetchUsers();
      setShowBulkApproveDialog(false);
      toast.success('Selected pending users approved');
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to approve users'));
    } finally { setActionLoading(false); }
  }, [fetchUsers]);

  const handleBulkSendInvitations = useCallback(async (profileIds: number[]) => {
    if (profileIds.length === 0) return;
    try {
      setActionLoading(true); setError(null);
      let ok = 0;
      let failed = 0;
      for (const id of profileIds) {
        try {
          const response = await coreAPI.sendInvitationEmail(id);
          ok += 1;
          if (response.data.user) {
            const updated = response.data.user;
            setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
          }
        } catch {
          failed += 1;
        }
      }
      await fetchUsers();
      if (failed === 0) {
        toast.success(`Invitation emails sent (${ok})`);
      } else if (ok === 0) {
        toast.error(`Failed to send ${failed} invitation(s)`);
      } else {
        toast.error(`Sent ${ok} invitation(s); ${failed} failed`);
      }
      setShowBulkInviteDialog(false);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Bulk invitation failed'));
    } finally { setActionLoading(false); }
  }, [fetchUsers]);

  return {
    users, availableRoles, activityLogs, userSitePermissions,
    loading, actionLoading, error, selectedUser, selectedRoleId,
    savingPermission, userDetailTab,
    showUserDetails, showChangeRoleDialog, showActivityLogDialog, showBulkApproveDialog,
    showBulkInviteDialog,
    setError, setSelectedUser, setSelectedRoleId, setUserDetailTab,
    setShowUserDetails, setShowChangeRoleDialog, setShowActivityLogDialog, setShowBulkApproveDialog,
    setShowBulkInviteDialog,
    fetchUsers,
    handleToggleActive, handleSafeDelete, handleChangeRole: handleChangeRoleImpl, handleToggleAdmin,
    handleSendInvitation, handleViewActivityLog,
    openChangeRoleDialog, openUserDetails,
    handleToggleSiteLink, handleToggleSitePermission,
    handleApproveUser, handleRejectUser, handleBulkApprove, handleBulkSendInvitations,
    fetchAvailableRoles,
  };
}
